import React, { Component } from 'react';

import { Icon, IconArrowDown, IconArrowLeft, IconArrowRight, IconArrowUp } from '../../../base/icons';
import {
    getAllParticipantAndSharedFrames,
    isSharedThumbnailEnabled
} from '../../../base/participants/functions';
import { connect } from '../../../base/redux';
import { LAYOUTS } from '../../../video-layout';
import { setVisibleParticipantsStartIndex } from '../../actions';
import { setCurrentTileViewPage } from '../../actions.web';

type Props = {
    _sharedTileCount: number,
    _tileViewTileLimit: number,
    _totalParticipants: number,
    _totalTiles: number,
    _startIndex: number,
    dispatch: Function
};

/**
 * Returns fixed remote-video page starts for tile view.
 *
 * Page 1 reserves one slot for local video. Later pages do not render local,
 * so they use the full effective tile limit.
 *
 * @param {number} remoteParticipants - Remote participant count.
 * @param {number} effectiveTileLimit - Tile limit after shared tiles are reserved.
 * @returns {Array<number>}
 */
function getPageStarts(remoteParticipants, effectiveTileLimit) {
    const firstPageSize = Math.max(1, effectiveTileLimit - 1);

    if (remoteParticipants <= firstPageSize) {
        return [ 0 ];
    }

    const pageStarts = [ 0 ];
    const laterPageSize = Math.max(1, effectiveTileLimit);
    let nextStart = firstPageSize;

    while (nextStart < remoteParticipants) {
        pageStarts.push(nextStart);
        nextStart += laterPageSize;
    }

    return pageStarts;
}


/**
 * Component that renders the tile view carousel navigation.
 */
class TileViewCarousel extends Component<Props> {
    /**
     * Page count last reported to the parent through _totalnoofPages.
     */
    _reportedPageCount: ?number;

    /**
     * Page last stored with setCurrentTileViewPage.
     */
    _reportedCurrentPage: ?number;

    /**
     *
     * @param {*} props
     */
    constructor(props: Props) {
        super(props);
        this._onPrevious = this._onPrevious.bind(this);
        this._onNext = this._onNext.bind(this);
        this.getCurrentPageIndex = this.getCurrentPageIndex.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._reportPages();
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     *
     * @inheritdoc
     */
    componentDidUpdate() {
        this._reportPages();
    }

    /**
     * Returns the page starts of the carousel, or a single page when the
     * tiles fit without paging.
     *
     * @returns {Array<number>}
     */
    _getPageStarts() {
        const { _sharedTileCount, _tileViewTileLimit, _totalParticipants, _totalTiles } = this.props;

        if (!_tileViewTileLimit || _totalTiles <= _tileViewTileLimit) {
            return [ 0 ];
        }

        const effectiveTileLimit = Math.max(1, _tileViewTileLimit - _sharedTileCount);
        const remoteParticipants = Math.max(0, _totalParticipants - 1);

        return getPageStarts(remoteParticipants, effectiveTileLimit);
    }

    /**
     * Passes the page count to the parent and the current page to redux.
     * Runs after render: both update state elsewhere, which render must not
     * do. Only changes are sent, as the parent's setState re-renders this
     * component and would otherwise loop.
     *
     * @returns {void}
     */
    _reportPages() {
        const pageStarts = this._getPageStarts();
        const pageCount = pageStarts.length;
        const currentPage = pageCount > 1 ? this.getCurrentPageIndex(pageStarts, this.props._startIndex) + 1 : 1;

        if (this.props._totalnoofPages && pageCount !== this._reportedPageCount) {
            this._reportedPageCount = pageCount;
            this.props._totalnoofPages(pageCount);
        }

        if (currentPage !== this._reportedCurrentPage) {
            this._reportedCurrentPage = currentPage;
            this.props.dispatch(setCurrentTileViewPage(currentPage));
        }
    }

    /**
     * Finds the current page index for a stored remote start index.
     *
     * @param {Array<number>} pageStarts - Valid page starts.
     * @param {number} startIndex - Stored carousel start index.
     * @returns {number}
     */
    getCurrentPageIndex(pageStarts, startIndex) {
        const normalizedStartIndex = Math.max(0, parseInt(startIndex, 10) || 0);
        let currentPageIndex = 0;

        for (let i = 0; i < pageStarts.length; i++) {
            if (pageStarts[i] > normalizedStartIndex) {
                break;
            }
            currentPageIndex = i;
        }

        return currentPageIndex;
    }

    /**
     * Renders the tile view carousel navigation.
     * @returns {React.Element}
     */
    render() {
        const { _startIndex, conferenceView, _filmStripView } = this.props;
        const pageStarts = this._getPageStarts();
        const totalPages = pageStarts.length;

        if (totalPages <= 1) {
            return null;
        }

        const currentPageIndex = this.getCurrentPageIndex(pageStarts, _startIndex);
        const currentPage = currentPageIndex + 1;
        const showPrevious = currentPageIndex > 0;
        const showNext = currentPageIndex < totalPages - 1;
        const _cs1 = { display: conferenceView === 'fixed' ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            color: '#fff',
            fontWeight: 'bold' };

        return _filmStripView === LAYOUTS.TILE_VIEW ? (

            <div
                aria-label = 'Tile view carousel navigation'
                className = { conferenceView === 'fixed' ? 'tile-view-carousel' : 'tile-view-carousel-float' }
                role = 'navigation'>
                <button
                    aria-label = 'Previous page'
                    className = { conferenceView === 'fixed' ? `carousel-nav carousel-nav-prev ${showPrevious ? '' : 'disabled'}` : `toolbox-icon ${showPrevious ? '' : 'disabled'}` }
                    disabled = { !showPrevious }
                    onClick = { this._onPrevious }
                    type = 'button'>
                    <svg
                        height = '24'
                        viewBox = '0 0 24 24'
                        width = '24'
                        xmlns = 'http://www.w3.org/2000/svg'>
                        <path
                            d = 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'
                            fill = 'currentColor' />
                    </svg>
                </button>
                <div
                    className = 'carousel-page-indicator'
                    style = { _cs1 }>
                    <span>Page {currentPage} of {Math.max(1, totalPages)}</span>
                </div>
                <button
                    aria-label = 'Next page'
                    className = { conferenceView === 'fixed' ? `carousel-nav carousel-nav-next ${showNext ? '' : 'disabled'}` : `toolbox-icon ${showNext ? '' : 'disabled'}` }
                    disabled = { !showNext }
                    onClick = { this._onNext }
                    type = 'button'>
                    <svg
                        height = '24'
                        viewBox = '0 0 24 24'
                        width = '24'
                        xmlns = 'http://www.w3.org/2000/svg'>
                        <path
                            d = 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'
                            fill = 'currentColor' />
                    </svg>
                </button>
            </div>)
            : (_filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW || _filmStripView === LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW) && <div className = { `tile_navigation ${_filmStripView}` }>
                <div
                    className = { `tile_navigation_button arrow-up ${showPrevious ? '' : 'disabled'}` }
                    onClick = { this._onPrevious } >
                    <Icon src = { _filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW ? IconArrowUp : IconArrowLeft } />
                </div>
                <div
                    className = { `tile_navigation_button arrow-down ${showNext ? '' : 'disabled'}` }
                    onClick = { this._onNext } >
                    <Icon src = { _filmStripView === LAYOUTS.VERTICAL_FILMSTRIP_VIEW ? IconArrowDown : IconArrowRight } />
                </div>
            </div>;
    }

    /**
     * Handles the previous button click.
     */
    _onPrevious() {
        const { _sharedTileCount, _tileViewTileLimit, _totalParticipants, _startIndex, dispatch } = this.props;
        const effectiveTileLimit = Math.max(1, _tileViewTileLimit - _sharedTileCount);
        const remoteParticipants = Math.max(0, _totalParticipants - 1);
        const pageStarts = getPageStarts(remoteParticipants, effectiveTileLimit);
        const currentPageIndex = this.getCurrentPageIndex(pageStarts, _startIndex);
        const newIndex = pageStarts[Math.max(0, currentPageIndex - 1)] || 0;

        dispatch(setVisibleParticipantsStartIndex(newIndex));
    }

    /**
     * Handles the next button click.
     */
    _onNext() {
        const { _sharedTileCount, _tileViewTileLimit, _totalParticipants, _startIndex, dispatch } = this.props;
        const effectiveTileLimit = Math.max(1, _tileViewTileLimit - _sharedTileCount);
        const remoteParticipants = Math.max(0, _totalParticipants - 1);
        const pageStarts = getPageStarts(remoteParticipants, effectiveTileLimit);
        const currentPageIndex = this.getCurrentPageIndex(pageStarts, _startIndex);
        const newIndex = pageStarts[Math.min(pageStarts.length - 1, currentPageIndex + 1)] || 0;

        dispatch(setVisibleParticipantsStartIndex(newIndex));
    }
}

/**
 * Maps part of Redux state to component props.
 * @param {Object} state - The Redux state.
 * @returns {Object}
 */
function _mapStateToProps(state) {
    const { tileViewTileLimit, visibleParticipantsStartIndex } = state['features/filmstrip'];
    const participants = state['features/base/participants'] || [];
    const participantsAndSharedThumbnails = getAllParticipantAndSharedFrames(state);
    const sharedTileCount = isSharedThumbnailEnabled(state) || state['features/shared-video']?.ownerId ? 1 : 0;
    const conferenceView = state['features/base/conference']?.conferenceView;

    return {
        _sharedTileCount: sharedTileCount,
        _tileViewTileLimit: tileViewTileLimit || 0,
        _startIndex: visibleParticipantsStartIndex || 0,
        _totalParticipants: participants.length,
        _totalTiles: participantsAndSharedThumbnails.length,
        conferenceView
    };
}

export default connect(_mapStateToProps)(TileViewCarousel);
